import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheck,
  faSpinner,
  faCircle,
  faSearch,
  faChartLine,
  faListUl,
  faFileText,
  faImage,
} from "@fortawesome/free-solid-svg-icons";
import {
  fetchArticleProgress,
  type ProgressData,
} from "@/services/articleProgress";
import { useArticleProgress } from "@/hooks/useArticleProgress";

interface ArticleProgressModalProps {
  postId: string | null;
  onComplete?: (postId: string) => void;
  onClose?: () => void;
}

const STAGE_CONFIG = {
  research: {
    threshold: 0,
    title: "Research",
    description: "Analyzing your brand and topic",
    icon: faSearch,
  },
  statistics: {
    threshold: 10,
    title: "Data",
    description: "Collecting relevant statistics and insights",
    icon: faChartLine,
  },
  outline: {
    threshold: 25,
    title: "Outline",
    description: "Structuring your article content",
    icon: faListUl,
  },
  content: {
    threshold: 50,
    title: "Content",
    description: "Generating the final article content",
    icon: faFileText,
  },
  image: {
    threshold: 75,
    title: "Image",
    description: "Creating a featured image for your article",
    icon: faImage,
  },
  draft: {
    threshold: 100,
    title: "Complete",
    description: "Article generation complete!",
    icon: faCheck,
  },
};

type StageStatus = "pending" | "in_progress" | "completed";

export default function ArticleProgressModal({
  postId,
  onComplete,
  onClose,
}: ArticleProgressModalProps) {
  const navigate = useNavigate();
  const [progressData, setProgressData] = useState<ProgressData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [countdown, setCountdown] = useState<number | null>(null);

  // Initial fetch
  useEffect(() => {
    if (!postId) {
      setIsLoading(false);
      return;
    }

    fetchArticleProgress(postId)
      .then((data) => {
        setProgressData(
          data || {
            progress: 0,
            currentStage: "research",
            message: "Starting generation...",
            isComplete: false,
          },
        );
        setIsLoading(false);
      })
      .catch(() => {
        setProgressData({
          progress: 0,
          currentStage: "research",
          message: "Starting generation...",
          isComplete: false,
          error: "Failed to load initial progress",
        });
        setIsLoading(false);
      });
  }, [postId]);

  // Subscribe to updates
  const unsubscribe = useArticleProgress(postId, setProgressData);

  // Handle completion
  useEffect(() => {
    if (progressData?.isComplete && !countdown && onComplete && postId) {
      setCountdown(3);
      onComplete(postId);
    }
  }, [progressData?.isComplete, countdown, onComplete, postId]);

  // Countdown timer
  useEffect(() => {
    if (countdown === null || countdown <= 0) return;

    const timer = setTimeout(() => {
      if (countdown === 1) {
        unsubscribe();
        navigate("/articles");
        onClose?.();
      } else {
        setCountdown(countdown - 1);
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [countdown, unsubscribe, navigate, onClose]);

  const handleClose = () => {
    unsubscribe();
    onClose?.();
  };

  const getStageStatus = (stageId: string): StageStatus => {
    if (!progressData) return "pending";

    const config = STAGE_CONFIG[stageId as keyof typeof STAGE_CONFIG];
    if (!config) return "pending";

    // Current stage is always in_progress (unless completed)
    if (stageId === progressData.currentStage && !progressData.isComplete) {
      return "in_progress";
    }

    // Check if this stage is completed based on progress
    // A stage is completed if we've moved past it
    const stageOrder = [
      "research",
      "statistics",
      "outline",
      "content",
      "image",
      "draft",
    ];
    const currentIndex = stageOrder.indexOf(progressData.currentStage);
    const thisIndex = stageOrder.indexOf(stageId);

    if (thisIndex < currentIndex) {
      return "completed";
    }

    return "pending";
  };

  const getStatusIcon = (status: StageStatus) => {
    switch (status) {
      case "completed":
        return faCheck;
      case "in_progress":
        return faSpinner;
      default:
        return faCircle;
    }
  };

  const getStatusColor = (status: StageStatus) => {
    switch (status) {
      case "completed":
        return "text-green-600";
      case "in_progress":
        return "text-blue-600";
      default:
        return "text-gray-400";
    }
  };

  const getStatusBgColor = (status: StageStatus) => {
    switch (status) {
      case "completed":
        return "bg-green-50 border-green-200";
      case "in_progress":
        return "bg-blue-50 border-blue-200";
      default:
        return "bg-gray-50 border-gray-100";
    }
  };

  if (!postId) return null;

  return (
    <Dialog
      open={true}
      onOpenChange={(open) => {
        if (!open && !progressData?.isComplete) {
          handleClose();
        }
      }}
    >
      <DialogContent
        className="sm:max-w-md"
        onPointerDownOutside={(e) =>
          progressData?.isComplete && e.preventDefault()
        }
      >
        {isLoading ? (
          <div className="flex items-center justify-center p-8">
            <FontAwesomeIcon
              icon={faSpinner}
              className="animate-spin h-8 w-8 text-blue-600 mr-3"
            />
            <span className="text-muted-foreground">Loading progress...</span>
          </div>
        ) : progressData ? (
          <>
            <DialogHeader>
              <DialogTitle>
                {progressData.isComplete
                  ? "Article Generation Complete!"
                  : "Generating Article"}
              </DialogTitle>
              <DialogDescription>
                {progressData.isComplete && countdown
                  ? `Redirecting to articles in ${countdown} seconds...`
                  : progressData.message}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-6 py-4">
              {/* Progress bar */}
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium">Overall Progress</span>
                  <span className="text-sm text-muted-foreground">
                    {progressData.progress}%
                  </span>
                </div>
                <div className="text-xs text-center text-muted-foreground mb-2">
                  Current Stage:{" "}
                  <span className="font-medium">
                    {STAGE_CONFIG[
                      progressData.currentStage as keyof typeof STAGE_CONFIG
                    ]?.title || progressData.currentStage}
                  </span>
                </div>
                <Progress
                  value={progressData.progress}
                  className="h-3 bg-gray-200"
                  indicatorClassName="bg-gradient-to-r from-blue-500 to-blue-600"
                />
              </div>

              {/* Stage indicators */}
              <div className="space-y-3">
                <h4 className="text-sm font-medium text-gray-700">
                  Progress Stages
                </h4>
                <div className="space-y-2">
                  {Object.entries(STAGE_CONFIG)
                    .filter(([key]) => key !== "draft")
                    .map(([stageId, config]) => {
                      const status = getStageStatus(stageId);
                      return (
                        <div
                          key={stageId}
                          className={`flex items-center space-x-3 p-3 rounded-lg transition-all duration-300 border ${getStatusBgColor(status)}`}
                        >
                          <div
                            className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                              status === "completed"
                                ? "bg-green-100"
                                : status === "in_progress"
                                  ? "bg-blue-100"
                                  : "bg-gray-100"
                            }`}
                          >
                            <FontAwesomeIcon
                              icon={
                                status === "pending"
                                  ? config.icon
                                  : getStatusIcon(status)
                              }
                              className={`h-4 w-4 ${getStatusColor(status)} ${
                                status === "in_progress" ? "animate-spin" : ""
                              }`}
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between">
                              <h5 className="text-sm font-medium">
                                {config.title}
                              </h5>
                              {status === "in_progress" && (
                                <div className="flex space-x-1">
                                  <div className="w-1 h-1 bg-blue-500 rounded-full animate-bounce"></div>
                                  <div
                                    className="w-1 h-1 bg-blue-500 rounded-full animate-bounce"
                                    style={{ animationDelay: "0.1s" }}
                                  ></div>
                                  <div
                                    className="w-1 h-1 bg-blue-500 rounded-full animate-bounce"
                                    style={{ animationDelay: "0.2s" }}
                                  ></div>
                                </div>
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground mt-1">
                              {config.description}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                </div>
              </div>

              {/* Error display */}
              {progressData.error && (
                <Alert variant="destructive">
                  <AlertDescription>{progressData.error}</AlertDescription>
                </Alert>
              )}
            </div>

            {/* Action button */}
            {!progressData.isComplete && (
              <DialogFooter>
                <Button variant="outline" onClick={handleClose}>
                  Continue in Background
                </Button>
              </DialogFooter>
            )}

            {progressData.isComplete && (
              <DialogFooter>
                <Button
                  onClick={() => {
                    unsubscribe();
                    navigate("/articles");
                    onClose?.();
                  }}
                >
                  View Articles {countdown ? `(${countdown})` : ""}
                </Button>
              </DialogFooter>
            )}
          </>
        ) : (
          <div className="p-8 text-center text-muted-foreground">
            No progress data available
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
